import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  MARQUEE_GESTURE_MIN_PX,
  type MarqueeDragState,
  collectRowIdsInMarquee,
  marqueeDragToBox,
} from '../../../shared/util/workListMarqueeUtils';

const AUTO_SCROLL_SPEED = 7;
const AUTO_SCROLL_EDGE_PX = 50;

interface UseAdminVenueListMarqueeOptions {
  venueListCompRef: RefObject<HTMLElement | null>;
  commitMarqueeVenueIds: (venueIds: string[]) => void;
  enabled: boolean;
}

export const useAdminVenueListMarquee = ({
  venueListCompRef,
  commitMarqueeVenueIds,
  enabled,
}: UseAdminVenueListMarqueeOptions) => {
  const [marqueeDrag, setMarqueeDrag] = useState<MarqueeDragState | null>(null);
  const [hitVenueIds, setHitVenueIds] = useState<string[]>([]);
  const dragRef = useRef<MarqueeDragState | null>(null);
  const exceededThresholdRef = useRef(false);
  const suppressNextRowClickRef = useRef(false);
  const autoScrollRafRef = useRef<number | null>(null);
  const autoScrollDirRef = useRef<'up' | 'down' | null>(null);
  const lastPointerRef = useRef<{ clientX: number; clientY: number } | null>(null);
  const originRef = useRef<{ x: number; y: number } | null>(null);
  const removeWindowListenersRef = useRef<(() => void) | null>(null);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRafRef.current !== null) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
    autoScrollDirRef.current = null;
  }, []);

  const setDrag = useCallback((drag: MarqueeDragState | null) => {
    dragRef.current = drag;
    setMarqueeDrag(drag);
  }, []);

  const collectVenueIds = useCallback((drag: MarqueeDragState | null) => {
    const container = venueListCompRef.current;
    const box = marqueeDragToBox(drag);
    if (!container || !box) return [];
    return collectRowIdsInMarquee(
      container,
      box,
      '[data-venue-id]',
      (element) => element.dataset.venueId,
    );
  }, [venueListCompRef]);

  const applyPointerToMarquee = useCallback((clientX: number, clientY: number) => {
    const container = venueListCompRef.current;
    const drag = dragRef.current;
    if (!container || !drag) return;
    const rect = container.getBoundingClientRect();
    const currentX = clientX - rect.left + container.scrollLeft;
    const currentY = clientY - rect.top + container.scrollTop;
    const origin = originRef.current;
    if (
      origin
      && (
        Math.abs(currentX - origin.x) >= MARQUEE_GESTURE_MIN_PX
        || Math.abs(currentY - origin.y) >= MARQUEE_GESTURE_MIN_PX
      )
    ) {
      exceededThresholdRef.current = true;
    }
    setDrag({ ...drag, currentX, currentY });
  }, [setDrag, venueListCompRef]);

  const startAutoScroll = useCallback((direction: 'up' | 'down') => {
    if (autoScrollDirRef.current === direction && autoScrollRafRef.current !== null) return;
    stopAutoScroll();
    autoScrollDirRef.current = direction;

    const scroll = () => {
      const container = venueListCompRef.current;
      if (!container) {
        stopAutoScroll();
        return;
      }
      const maxScroll = container.scrollHeight - container.clientHeight;
      const offset = direction === 'up' ? -AUTO_SCROLL_SPEED : AUTO_SCROLL_SPEED;
      container.scrollTop = Math.max(0, Math.min(maxScroll, container.scrollTop + offset));
      const pointer = lastPointerRef.current;
      if (pointer) applyPointerToMarquee(pointer.clientX, pointer.clientY);

      if (
        (direction === 'up' && container.scrollTop <= 0)
        || (direction === 'down' && container.scrollTop >= maxScroll)
      ) {
        stopAutoScroll();
        return;
      }
      autoScrollRafRef.current = requestAnimationFrame(scroll);
    };

    autoScrollRafRef.current = requestAnimationFrame(scroll);
  }, [applyPointerToMarquee, stopAutoScroll, venueListCompRef]);

  const syncAutoScroll = useCallback((clientY: number) => {
    const container = venueListCompRef.current;
    if (!container || !exceededThresholdRef.current) {
      stopAutoScroll();
      return;
    }
    const rect = container.getBoundingClientRect();
    if (clientY - rect.top < AUTO_SCROLL_EDGE_PX && container.scrollTop > 0) {
      startAutoScroll('up');
    } else if (
      rect.bottom - clientY < AUTO_SCROLL_EDGE_PX
      && container.scrollTop < container.scrollHeight - container.clientHeight
    ) {
      startAutoScroll('down');
    } else {
      stopAutoScroll();
    }
  }, [startAutoScroll, stopAutoScroll, venueListCompRef]);

  useLayoutEffect(() => {
    setHitVenueIds(collectVenueIds(marqueeDrag));
  }, [collectVenueIds, marqueeDrag]);

  useEffect(() => () => {
    removeWindowListenersRef.current?.();
    stopAutoScroll();
  }, [stopAutoScroll]);

  useEffect(() => {
    if (enabled) return;
    removeWindowListenersRef.current?.();
    removeWindowListenersRef.current = null;
    stopAutoScroll();
    dragRef.current = null;
    setMarqueeDrag(null);
    setHitVenueIds([]);
  }, [enabled, stopAutoScroll]);

  const handleVenueListClickCapture = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    if (!suppressNextRowClickRef.current) return;
    suppressNextRowClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleVenueListMouseDown = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    if (!enabled || event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target.closest('[data-suppress-marquee]')) return;
    const container = venueListCompRef.current;
    if (!container) return;

    event.preventDefault();
    exceededThresholdRef.current = false;
    lastPointerRef.current = { clientX: event.clientX, clientY: event.clientY };
    const rect = container.getBoundingClientRect();
    const originX = event.clientX - rect.left + container.scrollLeft;
    const originY = event.clientY - rect.top + container.scrollTop;
    originRef.current = { x: originX, y: originY };
    setDrag({ originX, originY, currentX: originX, currentY: originY });

    const onMove = (moveEvent: globalThis.MouseEvent) => {
      lastPointerRef.current = {
        clientX: moveEvent.clientX,
        clientY: moveEvent.clientY,
      };
      applyPointerToMarquee(moveEvent.clientX, moveEvent.clientY);
      syncAutoScroll(moveEvent.clientY);
    };
    const onUp = () => {
      const didDrag = exceededThresholdRef.current;
      if (didDrag) {
        suppressNextRowClickRef.current = true;
        commitMarqueeVenueIds(collectVenueIds(dragRef.current));
      }
      exceededThresholdRef.current = false;
      lastPointerRef.current = null;
      originRef.current = null;
      stopAutoScroll();
      setDrag(null);
      setHitVenueIds([]);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      removeWindowListenersRef.current = null;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    removeWindowListenersRef.current = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [
    applyPointerToMarquee,
    collectVenueIds,
    commitMarqueeVenueIds,
    enabled,
    setDrag,
    stopAutoScroll,
    syncAutoScroll,
    venueListCompRef,
  ]);

  return {
    hitVenueIds,
    handleVenueListMouseDown,
    handleVenueListClickCapture,
    marqueeBox: marqueeDragToBox(marqueeDrag),
    marqueeSelecting: marqueeDrag !== null,
  };
};
